import React from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import { Navbar2, Footer2 } from "../../components";
import SeriesNav from "../../components/labs/SeriesNav";
import Zoomable from "../../components/Zoomable/Zoomable";
import { getRouteMeta } from "../../seoData";

function DigitalForensicsHomelabTopology() {
  const { title, description: desc, image, url } = getRouteMeta(
    "/digital-forensics-homelab-topology"
  );

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} data-react-helmet="true"></meta>

        <meta property="og:type" content="website" data-react-helmet="true"></meta>
        <meta property="og:url" content={url} data-react-helmet="true"></meta>
        <meta property="og:title" content={title} data-react-helmet="true"></meta>
        <meta property="og:description" content={desc} data-react-helmet="true"></meta>
        <meta property="og:image" content={image} data-react-helmet="true"></meta>

        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta property="twitter:url" content={url} data-react-helmet="true"></meta>
        <meta property="twitter:title" content={title} data-react-helmet="true"></meta>
        <meta property="twitter:description" content={desc} data-react-helmet="true"></meta>
        <meta property="twitter:image" content={image} data-react-helmet="true"></meta>
      </Helmet>
      <Navbar2 />
      <section id="blogs">
        <div className="blogs main">
          <div className="blogs-header pb-4">
            <Link to="/projects/" className="blog-back-link">
              <i className="fa-solid fa-arrow-left"></i>&nbsp;Back to Projects
            </Link>
            <h2 className="section-title">Digital Forensics HomeLab</h2>
            <div className="blog-meta">
              <span>
                <i className="fa-solid fa-diagram-project"></i>Part 2: Network
                Topology
              </span>
            </div>
          </div>

          <SeriesNav current="/digital-forensics-homelab-topology/" />

          <p className="m-5">
            Part 1 listed the pieces of the lab. Here's how they're actually
            wired together. pfSense sits at the center as the firewall and
            gateway for the whole lab network. Everything else hangs off it,
            but not all in the same way.
          </p>

          <div className="topology-diagram">
            <Zoomable label="Network topology diagram">
            <svg viewBox="0 0 760 470" role="img" aria-labelledby="topology-title">
              <title id="topology-title">
                Network topology: pfSense connects to the domain controller,
                Kali Linux, and CAINE. The domain controller connects to two
                domain-joined Windows 11 clients, each of which also has a
                dashed DNS fallback line straight to pfSense.
              </title>

              {/* solid tree connections */}
              <g stroke="crimson" strokeWidth="2.5" fill="none">
                <line x1="400" y1="105" x2="220" y2="195" />
                <line x1="400" y1="105" x2="400" y2="195" />
                <line x1="400" y1="105" x2="580" y2="195" />
                <line x1="220" y1="265" x2="140" y2="365" />
                <line x1="220" y1="265" x2="320" y2="365" />
              </g>

              {/* dashed DNS-fallback connections, straight to pfSense */}
              <g stroke="#8fa3ad" strokeWidth="2.5" fill="none" strokeDasharray="7 6">
                <path d="M 100 365 Q 10 230 340 108" />
                <path d="M 360 365 Q 470 230 460 108" />
              </g>

              {/* pfSense */}
              <g>
                <rect x="325" y="35" width="150" height="70" rx="10" fill="#101820" stroke="crimson" strokeWidth="2" />
                <text x="400" y="65" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">pfSense</text>
                <text x="400" y="86" textAnchor="middle" fill="#c9d3d8" fontSize="12">Firewall / Gateway</text>
              </g>

              {/* DC */}
              <g>
                <rect x="145" y="195" width="150" height="70" rx="10" fill="#101820" stroke="crimson" strokeWidth="2" />
                <text x="220" y="225" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">DC</text>
                <text x="220" y="246" textAnchor="middle" fill="#c9d3d8" fontSize="12">Win Server 2022 · AD DS · DNS</text>
              </g>

              {/* Kali */}
              <g>
                <rect x="325" y="195" width="150" height="70" rx="10" fill="#101820" stroke="#8fa3ad" strokeWidth="2" />
                <text x="400" y="225" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">Kali Linux</text>
                <text x="400" y="246" textAnchor="middle" fill="#c9d3d8" fontSize="12">Not domain-joined</text>
              </g>

              {/* CAINE */}
              <g>
                <rect x="505" y="195" width="150" height="70" rx="10" fill="#101820" stroke="#8fa3ad" strokeWidth="2" />
                <text x="580" y="225" textAnchor="middle" fill="#ffffff" fontSize="17" fontWeight="700">CAINE</text>
                <text x="580" y="246" textAnchor="middle" fill="#c9d3d8" fontSize="12">Not domain-joined</text>
              </g>

              {/* Win11 A */}
              <g>
                <rect x="65" y="365" width="150" height="70" rx="10" fill="#101820" stroke="crimson" strokeWidth="2" />
                <text x="140" y="393" textAnchor="middle" fill="#ffffff" fontSize="16" fontWeight="700">Win11 Client A</text>
                <text x="140" y="413" textAnchor="middle" fill="#c9d3d8" fontSize="11">DNS: DC → pfSense (fallback)</text>
              </g>

              {/* Win11 B */}
              <g>
                <rect x="245" y="365" width="150" height="70" rx="10" fill="#101820" stroke="crimson" strokeWidth="2" />
                <text x="320" y="393" textAnchor="middle" fill="#ffffff" fontSize="16" fontWeight="700">Win11 Client B</text>
                <text x="320" y="413" textAnchor="middle" fill="#c9d3d8" fontSize="11">DNS: DC → pfSense (fallback)</text>
              </g>
            </svg>
            </Zoomable>
          </div>
          <div className="topology-legend">
            <span>
              <span className="legend-swatch"></span>Network / domain
              connection
            </span>
            <span>
              <span className="legend-swatch legend-swatch--dashed"></span>
              DNS fallback path
            </span>
          </div>

          <div>
            <p className="m-5">
              <span className="fw-bolder">pfSense</span> is the perimeter for
              the whole lab: it's the gateway everything else sits behind,
              and it segments the lab off from the rest of my home network.
            </p>

            <p className="m-5">
              <span className="fw-bolder">The domain controller</span> (Windows
              Server 2022) runs Active Directory Domain Services and is the
              primary DNS server for the domain. The two{" "}
              <span className="fw-bolder">Windows 11 clients</span> are joined
              to that domain and each run a logon script pushed down from the
              DC on sign-in. Each client is configured with two DNS servers,
              the DC first and pfSense as a fallback, so name resolution
              keeps working even if the DC is offline.
            </p>

            <p className="m-5">
              <span className="fw-bolder">Kali Linux</span> and{" "}
              <span className="fw-bolder">CAINE</span> both sit off pfSense
              directly and are deliberately{" "}
              <span className="fw-bolder">not</span> connected to the DC or
              joined to the domain. Kali is there to simulate attacker
              activity against the domain from the outside, and CAINE is
              there to acquire and analyze evidence afterward. Keeping both
              off the domain keeps the attack/analysis tooling separate from
              what's actually being tested.
            </p>

            <p className="m-5">
              <span className="fw-bolder">Up next:</span> Part 3 gets into
              Active Directory itself, what it is, the services that
              actually make it run, and the role it plays in incidents.
            </p>
          </div>

          <SeriesNav current="/digital-forensics-homelab-topology/" />
        </div>
        <Footer2 />
      </section>
    </div>
  );
}

export default DigitalForensicsHomelabTopology;
